import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  Wallet,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Building2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/tax";
import { RevealEmail } from "@/components/RevealEmail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Settings, Save } from "lucide-react";

export function exportFilingsCsv(setBusy) {
  setBusy && setBusy(true);
  return api
    .get("/admin/filings/export?reveal=true", { responseType: "blob" })
    .then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `cheltenham-filings-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    })
    .catch(() => toast.error("Could not export CSV"))
    .finally(() => setBusy && setBusy(false));
}

const SummaryCard = ({ icon: Icon, label, value, tone, testId }) => {
  const tones = {
    blue: "bg-blue-50 text-[#1E3A8A]",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone] || tones.blue}`}>
          <Icon className="w-5 h-5" />
        </span>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
          <p className="font-mono-data text-2xl font-semibold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default function AdminPayments() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvBusy, setCsvBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | paid | unpaid
  const [paySettings, setPaySettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/payments/summary"), api.get("/filings"), api.get("/settings/payments")])
      .then(([sRes, fRes, pRes]) => {
        setSummary(sRes.data);
        setFilings(fRes.data);
        setPaySettings(pRes.data);
      })
      .catch(() => toast.error("Failed to load payments"))
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings/payments", {
        payments_enabled: paySettings.payments_enabled,
        mail_instructions: paySettings.mail_instructions || "",
      });
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const payable = filings.filter((f) => (f.totals?.l14 || 0) > 0);
  const filtered = payable.filter((f) => {
    if (filter === "paid") return f.payment_status === "paid";
    if (filter === "unpaid") return f.payment_status !== "paid";
    return true;
  });

  if (loading)
    return (
      <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div>
      {paySettings && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="payment-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-[#1E3A8A]" />
            <h2 className="font-heading text-xl tracking-tight font-medium text-slate-900">Payment Settings</h2>
          </div>
          <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-md">
            <div>
              <p className="font-medium text-slate-900">Accept online payments</p>
              <p className="text-sm text-slate-500 mt-0.5">
                {paySettings.payments_enabled
                  ? "Merchants can pay by card online, or choose to mail a check."
                  : "Online payment is off. Merchants submit their filing and mail in payment."}
              </p>
            </div>
            <Switch
              checked={!!paySettings.payments_enabled}
              onCheckedChange={(v) => setPaySettings((s) => ({ ...s, payments_enabled: v }))}
              data-testid="toggle-payments-enabled"
            />
          </div>
          <div className="space-y-2 mt-4">
            <Label>Mail-in payment instructions (shown to merchants)</Label>
            <Textarea
              rows={5}
              value={paySettings.mail_instructions || ""}
              onChange={(e) => setPaySettings((s) => ({ ...s, mail_instructions: e.target.value }))}
              data-testid="mail-instructions-input"
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={saveSettings}
              disabled={savingSettings}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              data-testid="save-payment-settings"
            >
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save settings
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Payments</h2>
          <p className="text-sm text-slate-500 mt-1">Card payments collected and balances outstanding.</p>
        </div>
        <Button
          onClick={() => exportFilingsCsv(setCsvBusy)}
          disabled={csvBusy}
          variant="outline"
          className="border-slate-300"
          data-testid="payments-export-csv"
        >
          {csvBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard icon={Wallet} label="Collected" value={money(summary?.total_collected)} tone="green" testId="pay-collected" />
        <SummaryCard icon={AlertCircle} label="Outstanding" value={money(summary?.total_outstanding)} tone="amber" testId="pay-outstanding" />
        <SummaryCard icon={DollarSign} label="Total Assessed" value={money(summary?.total_assessed)} tone="blue" testId="pay-assessed" />
        <SummaryCard icon={CheckCircle2} label="Paid / Unpaid" value={`${summary?.paid_count ?? 0} / ${summary?.unpaid_count ?? 0}`} tone="blue" testId="pay-counts" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {[
          ["all", "All"],
          ["paid", "Paid"],
          ["unpaid", "Owes"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`payments-filter-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              filter === k ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="payments-empty">
          <p className="text-slate-500">No filings match this filter.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="payments-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-3 font-medium">Business</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Filer</th>
                <th className="px-5 py-3 font-medium text-right">Amount Due</th>
                <th className="px-5 py-3 font-medium text-center">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50 transition-colors" data-testid={`payment-row-${f.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-[#1E3A8A]" />
                      </span>
                      <div>
                        <p className="font-medium text-slate-900">{f.business_name}</p>
                        <p className="text-xs text-slate-400 font-mono-data">{f.confirmation_number}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 hidden md:table-cell">
                    <RevealEmail
                      masked={f.filer_email}
                      testId={`payment-email-${f.id}`}
                      onReveal={async () => {
                        const { data } = await api.get(`/admin/filings/${f.id}/email`);
                        return data.email;
                      }}
                    />
                  </td>
                  <td className="px-5 py-3 text-right font-mono-data font-medium text-slate-900">{money(f.totals?.l14)}</td>
                  <td className="px-5 py-3 text-center">
                    {f.payment_status === "paid" ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50">
                        <Clock className="w-3 h-3 mr-1" /> Owes
                      </Badge>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="outline" size="sm" className="border-slate-300" onClick={() => navigate(`/filing/${f.id}`)} data-testid={`payment-view-${f.id}`}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
